import { useState, useEffect, useCallback, useMemo } from "react";
import {
  format,
  getDate,
  getDaysInMonth,
  lastDayOfMonth,
  parseISO,
} from "date-fns";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useCompanyStore } from "@/common/states/store/Company";
import { DashboardService, DataProcessorService } from "../service";
import type { KPISlice } from "../service";
import { useLeadFilters } from "@/modules/leads/hooks/useLeadFilters";
import { useAppStore } from "@/common/states/store/slices";

const today = new Date();

/** Commercial dashboard: auto-refresh while the page is mounted (15 min). Skips when the browser tab is hidden. */
const COMMERCIAL_DASHBOARD_AUTO_REFRESH_MS = 15 * 60 * 1000;

type LeadsByTime = Record<string, number>;

type LeadsByOrigin = {
  origin: string;
  count: number;
  companyName?: string;
}[];

type LeadsByConsultant = {
  consultant: string;
  count: number;
  city?: string;
}[];

export type LeadsByCompany = {
  _id?: string;
  companyName?: string;
  status?: string;
  count: number;
};

type DashboardData = {
  todaySlice: KPISlice;
  filterSlice: KPISlice;
  activationsFilter: number;
  reactivationsFilter: number;
  activationsToday: number;
  reactivationsToday: number;
  plansTotals: Record<string, number>;
  plansTooltips: Record<string, string>;
  tma: number;
  leadsByTime: LeadsByTime;
  dailyGoal: number;
  monthGoal: number;
  leadsByOrigin: LeadsByOrigin;
  leadsByConsultant: LeadsByConsultant;
  leadsSixtyDaysAgo: Record<string, number>;
  leadsByCompany: LeadsByCompany[];
  leadsByCompanyFilter: LeadsByCompany[];
  activationsByConsultant: {
    activations: number;
    reactivations: number;
    technicalConsultant: string;
    city?: string;
  }[];
} | null;

export function useDashLead() {
  const { notifyError } = useToastContext();
  const { filterMode, selectedCompanies, selectedRegion, selectedUf } =
    useCompanyStore();
  const { filters, isFiltering, setLoadingFilters } = useLeadFilters();

  const [data, setData] = useState<DashboardData>(null);
  const [todaySlice, setTodaySlice] = useState<KPISlice | null>(null);
  const [filterSlice, setFilterSlice] = useState<KPISlice | null>(null);
  const [displayCompanyName, setDisplayCompanyName] =
    useState("OPERAÇÃO BRASIL");
  const [loading, setLoading] = useState(false);

  const fetchAllData = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (silent) {
        setLoadingFilters(true);
      } else {
        setLoading(true);
      }
      try {
        const { dateRange } = useAppStore.getState().dashboard.leads.filters;
        if (!dateRange.start || !dateRange.end) return;

        const result = await DashboardService.fetchDashboardData({
          startDate: dateRange.start,
          endDate: dateRange.end,
        });

        setData(result);
        setTodaySlice(result.todaySlice);
        setFilterSlice(result.filterSlice);
      } catch {
        notifyError("Erro ao buscar dados do dashboard");
      } finally {
        setLoadingFilters(false);
        if (!silent) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      notifyError,
      setLoadingFilters,
      filterMode,
      selectedCompanies,
      selectedRegion,
      selectedUf,
    ]
  );

  useEffect(() => {
    if (filterMode === "all") {
      setDisplayCompanyName("OPERAÇÃO BRASIL");
    } else if (filterMode === "region" && selectedRegion) {
      setDisplayCompanyName(`OPERAÇÃO ${selectedRegion}`);
    } else if (filterMode === "uf" && selectedUf) {
      setDisplayCompanyName(`OPERAÇÃO ${selectedUf}`);
    } else if (filterMode === "cities") {
      if (selectedCompanies.length === 1) {
        const name = selectedCompanies[0]?.companyName?.trim();
        setDisplayCompanyName(name ?? "OPERAÇÃO BRASIL");
      } else if (selectedCompanies.length > 1) {
        setDisplayCompanyName(
          `MÚLTIPLAS CIDADES (${selectedCompanies.length})`
        );
      } else {
        setDisplayCompanyName("OPERAÇÃO BRASIL"); // Fallback
      }
    }
  }, [filterMode, selectedCompanies, selectedRegion, selectedUf]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, selectedCompanies, selectedRegion, selectedUf]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void fetchAllData({ silent: true });
    }, COMMERCIAL_DASHBOARD_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchAllData]);

  const kpisToday: KPISlice =
    todaySlice ?? DataProcessorService.createDefaultSlice();
  const kpisFilter: KPISlice =
    filterSlice ?? DataProcessorService.createDefaultSlice();

  const plansTotals = useMemo(() => kpisFilter.plansTotals, [kpisFilter]);
  const plansTooltips = useMemo(() => kpisFilter.plansTooltips, [kpisFilter]);

  const activationsToday = data?.activationsToday ?? 0;
  const reactivationsToday = data?.reactivationsToday ?? 0;
  const tma = data?.tma;

  const activationsFilter = data?.activationsFilter ?? 0;
  const reactivationsFilter = data?.reactivationsFilter;
  const activationsByConsultant = data?.activationsByConsultant ?? [];

  const leadsByTime = data?.leadsByTime ?? {};
  const leadsByOrigin = [...(data?.leadsByOrigin ?? [])];
  const leadsByConsultant = [...(data?.leadsByConsultant ?? [])];
  const leadsByCompany = [...(data?.leadsByCompany ?? [])];
  const leadsByCompanyFilter = [...(data?.leadsByCompanyFilter ?? [])];

  const projectionUntilToday = {
    label: `Projeção até ${format(lastDayOfMonth(today), "dd/MM/yy")}`,
    value: Math.round(
      (activationsFilter / getDate(today)) * getDaysInMonth(today)
    ),
  };

  const startDate = filters.dateRange.start
    ? format(parseISO(filters.dateRange.start), "yyyy-MM-dd")
    : "";

  const endDate = filters.dateRange.end
    ? format(parseISO(filters.dateRange.end), "yyyy-MM-dd")
    : "";

  return {
    startDate,
    endDate,
    quickFilter: filters.quickFilter,
    isFiltering,
    displayCompanyName,
    plansTotals,
    plansTooltips,
    loading,
    kpisToday,
    kpisFilter,
    activationsFilter,
    reactivationsFilter,
    activationsByConsultant,
    activationsToday,
    reactivationsToday,
    tma,
    leadsByTime,
    leadsByOrigin,
    leadsByConsultant,
    leadsByCompany,
    leadsByCompanyFilter,
    projectionUntilToday,
    dailyGoal: data?.dailyGoal,
    monthGoal: data?.monthGoal,
    refetch: fetchAllData,
  };
}
